/**
 * menuConfig.js
 * Shared navigation configuration for Sidebar and AppLauncher.
 * Organized by domain groups for logical clarity.
 */

export const DOMAIN_GROUPS = [
  {
    key: 'physical',
    label: 'Infraestructura F\u00EDsica',
    sectionLabel: 'INFRAESTRUCTURA',
    color: '#3b82f6',
    emoji: '🏢',
    items: [
      { key: 'locations',          label: 'Ubicaciones',         path: '/locations',                          implemented: true  },
      { key: 'contacts',           label: 'Contactos',           path: '/contacts',                           implemented: true  },
      // «CPD» agrupa el armario y su entorno (rack, energía, cableado pasivo y
      // planos). Las seis entradas de dentro siguen existiendo —el buscador y
      // los favoritos las encuentran— pero NO se pintan sueltas en el menú: se
      // llega a ellas desde el menú del módulo.
      { key: 'datacenter',         label: 'CPD',                  path: '/infrastructure/racks',               implemented: true  },
      { key: 'racks',              label: 'Racks',               path: '/infrastructure/racks',               implemented: true, menuHidden: true },
      { key: 'servers',            label: 'Servidores',          path: '/infrastructure/servers',             implemented: true  },
      { key: 'chassis',            label: 'Chasis multinodo',    path: '/infrastructure/chassis',             implemented: true  },
      { key: 'storage',            label: 'Almacenamiento',      path: '/infrastructure/storage-arrays',      implemented: true  },
      { key: 'pdus',               label: 'PDUs',                path: '/infrastructure/pdus',                implemented: true, menuHidden: true },
      { key: 'ups',                label: 'SAI (torre y rack)',  path: '/infrastructure/ups',                  implemented: true, menuHidden: true },
      { key: 'patch-panels',       label: 'Patch Panels',        path: '/infrastructure/patch-panels',        implemented: true, menuHidden: true },
      { key: 'workstations',       label: 'Est. de Trabajo',     path: '/infrastructure/workstations',        implemented: true  },
      { key: 'ip-phones',          label: 'Tel\u00E9fonos IP',        path: '/infrastructure/ip-phones',           implemented: true  },
      { key: 'printers',           label: 'Impresoras',          path: '/infrastructure/printers',            implemented: true  },
      { key: 'peripherals',         label: 'Otros Periféricos',   path: '/infrastructure/peripherals',         implemented: true  },
      { key: 'access-points',       label: 'Accesos de dispositivo', path: '/infrastructure/accesses',         implemented: true  },
      { key: 'datacenter-diagram', label: 'Diagrama Datacenter', path: '/infrastructure/datacenter-diagram',  implemented: true, menuHidden: true },
      { key: 'power-canvas',       label: 'Canvas Alimentaci\u00F3n', path: '/infrastructure/power-canvas',      implemented: true, menuHidden: true },
    ],
  },
  {
    key: 'network-equipment',
    label: 'Equipos de Red',
    sectionLabel: null, // continues INFRAESTRUCTURA section
    color: '#10b981',
    emoji: '🔌',
    items: [
      { key: 'switches',          label: 'Switches',        path: '/infrastructure/switches',          implemented: true  },
      { key: 'firewalls',         label: 'Firewalls',       path: '/infrastructure/firewalls',         implemented: true  },
      { key: 'network-canvas',    label: 'Canvas Cableado', path: '/infrastructure/network-canvas',    implemented: true  },
      { key: 'network-topology',    label: 'Topología Red',      path: '/infrastructure/network-topology',  implemented: true  },
      { key: 'network-discovered',  label: 'Descubiertos',       path: '/infrastructure/network-discovered', implemented: true  },
      { key: 'connections-matrix',  label: 'Matriz Conexiones',  path: '/infrastructure/connections',       implemented: true  },
    ],
  },
  /**
   * Cableado era UNA página con cinco pestañas internas: la cabecera de la
   * página, la barra de pestañas y la cabecera de la sección se comían ~200 px
   * de alto y en un portátil de 14" no quedaba sitio para las filas. Ahora cada
   * vista es una entrada del menú (mismo componente, sin pestañas): se llega en
   * un clic y no se paga ese alto. Las rutas antiguas redirigen (ver App.jsx).
   */
  {
    key: 'cabling',
    label: 'Cableado',
    sectionLabel: null, // continues INFRAESTRUCTURA
    color: '#0ea5e9',
    emoji: '🧵',
    items: [
      { key: 'cabling-designer', label: 'Diseñador de cableado', path: '/cabling/designer', implemented: true },
      { key: 'cabling-wiring',   label: 'Diseñador de switches', path: '/cabling/wiring',   implemented: true },
      { key: 'cabling-map',      label: 'Mapa de planta',        path: '/cabling/map',      implemented: true },
      { key: 'cabling-outlets',  label: 'Tomas de pared',        path: '/cabling/outlets',  implemented: true },
      { key: 'cabling-canvas',   label: 'Tomas ↔ panel',         path: '/cabling/canvas',   implemented: true },
    ],
  },
  {
    key: 'ipam',
    label: 'Redes / IPAM',
    sectionLabel: 'CONECTIVIDAD',
    color: '#8b5cf6',
    emoji: '🌐',
    items: [
      { key: 'networks-dashboard', label: 'Resumen',       path: '/networks',              implemented: true },
      { key: 'networks-blocks',    label: 'Bloques',        path: '/networks/blocks',       implemented: true },
      { key: 'networks-subnets',   label: 'Subredes',       path: '/networks/subnets',      implemented: true },
      { key: 'networks-explorer',  label: 'Explorer',       path: '/networks/explorer',     implemented: true },
      { key: 'networks-assign',    label: 'Asignaciones',   path: '/networks/assignments',  implemented: true },
      { key: 'networks-vlans',     label: 'VLANs',          path: '/networks/vlans',        implemented: true },
      { key: 'networks-ips',       label: 'IPs',            path: '/networks/ips',          implemented: true },
      { key: 'networks-vips',      label: 'VIPs / CARP',    path: '/networks/vips',         implemented: true },
      { key: 'networks-dhcp',      label: 'DHCP',           path: '/networks/dhcp',         implemented: true },
    ],
  },
  {
    key: 'wifi',
    label: 'WiFi',
    sectionLabel: null, // continues CONECTIVIDAD
    color: '#f59e0b',
    emoji: '📡',
    items: [
      { key: 'wifi-dashboard',    label: 'Vista General',     path: '/wifi/dashboard',      implemented: true },
      { key: 'wifi-topology',     label: 'Topolog\u00EDa',         path: '/wifi/topology',       implemented: true },
      { key: 'wifi-map',          label: 'Mapa de planta',    path: '/wifi/map',            implemented: true },
      { key: 'wifi-controllers',  label: 'Controladores',     path: '/wifi/controllers',    implemented: true },
      { key: 'wifi-aps',          label: 'Puntos de Acceso',  path: '/wifi/aps',            implemented: true },
      { key: 'wifi-radius',       label: 'Servidores RADIUS', path: '/wifi/radius-servers', implemented: true },
    ],
  },
  {
    key: 'wan',
    label: 'WAN / ISP',
    sectionLabel: null, // continues CONECTIVIDAD
    color: '#ef4444',
    emoji: '🔗',
    items: [
      { key: 'wan-operators', label: 'Operadores ISP', path: '/wan/operators', implemented: true },
      { key: 'wan-circuits',  label: 'Circuitos',      path: '/wan/circuits',  implemented: true },
      { key: 'wan-routers',   label: 'Routers WAN',    path: '/wan/routers',   implemented: true },
    ],
  },
  {
    key: 'cameras',
    label: 'Cámaras de seguridad',
    sectionLabel: null, // continues CONECTIVIDAD
    color: '#0ea5e9',
    emoji: '📷',
    items: [
      { key: 'cameras-topology', label: 'Topología',       path: '/cameras/topology', implemented: true },
      { key: 'cameras-map',      label: 'Mapa de planta',  path: '/cameras/map',      implemented: true },
      { key: 'cameras-recorders', label: 'Grabadores',     path: '/cameras/recorders', implemented: true },
      { key: 'cameras-list',     label: 'Cámaras',         path: '/cameras/list',     implemented: true },
    ],
  },
  {
    key: 'dns',
    label: 'DNS',
    sectionLabel: null, // continues CONECTIVIDAD
    color: '#0891b2',
    emoji: '🌐',
    items: [
      { key: 'dns-domains', label: 'Dominios y registros', path: '/dns', implemented: true },
      { key: 'certificates', label: 'Certificados', path: '/certificates', implemented: true },
    ],
  },
  {
    key: 'virtualization',
    label: 'Virtualizaci\u00F3n',
    sectionLabel: 'VIRTUALIZACIÓN',
    color: '#6366f1',
    emoji: '💻',
    items: [
      { key: 'virt-overview',    label: 'Resumen',            path: '/virt/overview',    implemented: true },
      { key: 'virt-topology',    label: 'Topolog\u00EDa',          path: '/virt/topology',    implemented: true },
      { key: 'virt-hypervisors', label: 'Hypervisores',       path: '/virt/hypervisors', implemented: true },
      { key: 'virt-clusters',    label: 'Clusters',           path: '/virt/clusters',    implemented: true },
      { key: 'virt-datastores',  label: 'Datastores',         path: '/virt/datastores',  implemented: true },
      { key: 'virt-vswitches',   label: 'vSwitches',          path: '/virt/vswitches',   implemented: true },
      { key: 'virt-vms',         label: 'M\u00E1quinas Virtuales', path: '/virt/vms',         implemented: true },
      { key: 'virt-cloud',       label: 'Cloud / VPS',        path: '/virt/cloud',       implemented: true },
    ],
  },
  {
    key: 'services',
    label: 'Servicios & Apps',
    sectionLabel: 'SERVICIOS',
    color: '#f97316',
    emoji: '🛠️',
    items: [
      { key: 'services-designer', label: 'Dise\u00F1ador de servicios', path: '/services/designer', implemented: true },
      { key: 'services-list',   label: 'Cat\u00E1logo',              path: '/services',                    implemented: true },
      { key: 'services-depmap', label: 'Mapa de Dependencias',  path: '/services/dependency-map',     implemented: true },
      { key: 'service-farms',   label: 'Granjas de Servidores', path: '/services/farms',              implemented: true },
      { key: 'backup-policies', label: 'Pol\u00EDticas de Backup',   path: '/backups',                     implemented: true },
      { key: 'db-instances',    label: 'Bases de Datos',        path: '/apps/databases',              implemented: true },
      { key: 'licenses',        label: 'Licencias',             path: '/apps/licenses',               implemented: true },
      { key: 'web-sites',       label: 'Sitios Web',            path: '/apps/web-sites',              implemented: true },
    ],
  },
  {
    key: 'admin',
    label: 'Administraci\u00F3n',
    sectionLabel: 'GESTIÓN',
    color: '#64748b',
    emoji: '⚙️',
    items: [
      { key: 'roles',          label: 'Roles y Permisos',    path: '/admin/roles',          implemented: true },
      { key: 'naming',         label: 'Nomenclatura',         path: '/admin/naming',         implemented: true },
      { key: 'device-catalog', label: 'Catálogo de dispositivos', path: '/admin/catalog',    implemented: true },
      { key: 'setup',          label: 'Setup',                path: '/admin/setup',          implemented: true },
      { key: 'academia',       label: 'Academia CMDB',       path: '/academia',             implemented: true },
      { key: 'manual-cmdb',    label: 'Manual CMDB',       path: '/manual',               implemented: true },
      { key: 'feedback-hub',   label: 'Feedback Hub',         path: '/feedback',             implemented: true },
      { key: 'vulnerabilities', label: 'Vulnerabilidades',   path: '/security/vulnerabilities', implemented: true },
      { key: 'dns-providers',  label: 'Proveedores DNS',    path: '/admin/dns',           implemented: true, superadminOnly: true },
      { key: 'agent-settings', label: 'Agente CMDB',       path: '/settings/agent',       implemented: true },
      { key: 'agent-mapping',  label: 'Mapping Agente',    path: '/admin/agent-mapping',  implemented: true },
    ],
  },
];

/** Flat list of all items for search */
export const ALL_ITEMS = DOMAIN_GROUPS.flatMap(group =>
  group.items.map(item => ({ ...item, domainKey: group.key, domainLabel: group.label, domainColor: group.color }))
);

/**
 * Entradas que el sidebar pinta sueltas, fuera de un grupo acordeón.
 * Viven aquí para que el personalizador de menú también las liste y se puedan
 * ocultar como cualquier otra.
 */
export const STANDALONE_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', path: '/dashboard', implemented: true },
  // Planes de trabajo no es «infraestructura física»: es una tarea transversal
  // que puede caer sobre estaciones, servidores, VMs o firewalls a la vez.
  { key: 'work-plans', label: 'Planes de trabajo', path: '/work-plans', implemented: true },
  { key: 'company-docs', label: 'Documentación', path: '/documentation-empresa', implemented: true },
  // Contactos va a nivel general, justo detrás de Ubicaciones: es transversal a
  // todos los módulos (servidores, VMs, servicios, cámaras…).
  { key: 'contacts', label: 'Contactos', path: '/contacts', implemented: true },
];

/** Panel de administración de plataforma: solo para el superadmin global. */
export const PLATFORM_ADMIN_ITEM = {
  key: 'platform-admin',
  label: '🛡️ Plataforma CMDB',
  path: '/admin/platform',
  implemented: true,
  superadminOnly: true,
};

/**
 * Orden exacto del menú lateral: por cada tramo, su etiqueta de sección (o
 * null) y las entradas que van dentro, ya sean enlaces sueltos o grupos
 * acordeón de DOMAIN_GROUPS.
 */
export const MENU_SECTIONS = [
  { key: 'general', label: null, entries: [{ type: 'link', key: 'dashboard' }, { type: 'link', key: 'work-plans' }, { type: 'link', key: 'company-docs' }] },
  {
    key: 'infrastructure',
    label: 'INFRAESTRUCTURA',
    entries: [
      { type: 'link', key: 'locations' },
      { type: 'link', key: 'contacts' },
      { type: 'group', key: 'physical', skipItems: ['locations', 'contacts'] },
      { type: 'group', key: 'network-equipment' },
      { type: 'group', key: 'cabling' },
    ],
  },
  {
    key: 'connectivity',
    label: 'CONECTIVIDAD',
    entries: [
      { type: 'group', key: 'ipam' },
      { type: 'group', key: 'wifi' },
      { type: 'group', key: 'cameras' },
      { type: 'group', key: 'dns' },
      { type: 'group', key: 'wan' },
    ],
  },
  { key: 'virtualization', label: 'VIRTUALIZACIÓN', entries: [{ type: 'group', key: 'virtualization' }] },
  { key: 'services', label: 'SERVICIOS', entries: [{ type: 'group', key: 'services' }] },
  { key: 'management', label: 'GESTIÓN', entries: [{ type: 'group', key: 'admin' }] },
];

/** Resuelve una clave de menú a su entrada (enlace suelto o dentro de un grupo). */
export function findMenuItem(key) {
  const standalone = STANDALONE_ITEMS.find(item => item.key === key);
  if (standalone) return standalone;
  if (key === PLATFORM_ADMIN_ITEM.key) return PLATFORM_ADMIN_ITEM;
  for (const group of DOMAIN_GROUPS) {
    const found = group.items.find(item => item.key === key);
    if (found) return found;
  }
  return null;
}

/**
 * Grupos que muestra el personalizador de menú ("General" primero para
 * Dashboard). No incluye las entradas superadmin-only: se añaden aparte solo
 * cuando el usuario es superadmin.
 */
export const MENU_CUSTOMIZER_GROUPS = [
  { key: 'general', label: 'General', color: '#64748b', emoji: '🏠', items: STANDALONE_ITEMS },
  ...DOMAIN_GROUPS,
];

// ─────────────────────────────────────────────────────────────────────────────
// Modo "secciones" (menú compacto) y favoritos
// ─────────────────────────────────────────────────────────────────────────────
//
// El menú compacto muestra solo los títulos de sección; al entrar en una se
// despliega su contenido. Para que los enlaces directos (favoritos, resultados
// del buscador, historial) funcionen sin pasar por el menú, la sección y el
// grupo activos se deducen SIEMPRE de la ruta, no del último clic.

/** Tope de favoritos por ámbito; coincide con el del backend. */
export const FAVORITES_LIMIT = 20;

/** Todas las entradas del menú, incluidas las sueltas y la de plataforma. */
function allMenuItems() {
  return [...STANDALONE_ITEMS, ...ALL_ITEMS, PLATFORM_ADMIN_ITEM];
}

/**
 * Rutas que no tienen entrada propia en el menú pero pertenecen a una (fichas
 * de detalle, rutas antiguas). La estrella de favoritos debe marcar la entrada
 * de la que cuelgan, no quedarse muda en una ficha.
 */
const PATH_ALIASES = [
  { prefix: '/infrastructure/rack/', key: 'racks' },
  { prefix: '/rack/', key: 'racks' },
  { prefix: '/racks/', key: 'racks' },
];

/**
 * Clave de menú de la ruta actual, o null si la página no es una entrada del
 * menú (perfil, editor de documentos…). Gana el path más específico: en
 * `/networks/ips` devuelve `networks-ips`, no `networks-dashboard`.
 */
export function resolveMenuKeyForPath(pathname) {
  if (!pathname || typeof pathname !== 'string') return null;
  const clean = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';

  let best = null;
  for (const item of allMenuItems()) {
    if (!item?.path) continue;
    if (clean === item.path || clean.startsWith(`${item.path}/`)) {
      if (!best || item.path.length > best.path.length) best = item;
    }
  }
  if (best) return best.key;

  const alias = PATH_ALIASES.find((entry) => clean.startsWith(entry.prefix));
  return alias ? alias.key : null;
}

/** Sección (de MENU_SECTIONS) a la que pertenece una entrada del menú. */
export function findSectionForMenuKey(menuKey) {
  if (!menuKey) return null;
  for (const section of MENU_SECTIONS) {
    for (const entry of section.entries) {
      if (entry.type === 'link' && entry.key === menuKey) return section;
      if (entry.type === 'group') {
        const group = DOMAIN_GROUPS.find((g) => g.key === entry.key);
        if (group?.items.some((item) => item.key === menuKey)) return section;
      }
    }
  }
  return null;
}

/** Grupo de DOMAIN_GROUPS al que pertenece una entrada (null si es suelta). */
export function findGroupForMenuKey(menuKey) {
  if (!menuKey) return null;
  return DOMAIN_GROUPS.find((group) => group.items.some((item) => item.key === menuKey)) || null;
}

/**
 * Favorito normalizado a partir de una entrada del menú. Es lo que se guarda:
 * la clave para icono/visibilidad, y la ruta y la etiqueta por si la entrada
 * cambia de nombre en el futuro.
 */
export function buildFavoriteFromMenuItem(item, label) {
  if (!item?.key || !item?.path) return null;
  return {
    menu_key: item.key,
    path: item.path,
    label: label || item.label || null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Menús de MÓDULO (contextuales)
// ─────────────────────────────────────────────────────────────────────────────
//
// Cuando la ruta entra en uno de estos prefijos, el sidebar deja de enseñar el
// árbol entero y muestra SOLO lo del módulo, con dos accesos hacia arriba:
// «Todas las secciones» y la sección padre («Infraestructura»).
//
// POR QUÉ: Estaciones de trabajo ya no es una lista suelta. Tiene dashboard,
// listado, actualizaciones, software y planes, y con el menú completo el
// usuario tenía que buscar cada cosa en su sección de origen (el software
// vivía en Agente CMDB, los planes en Gestión…). Aquí están juntos y el
// camino de vuelta está a un clic.
//
// Regla: estas entradas NO van a `ALL_ITEMS` a propósito. No son secciones del
// menú global ni se pueden marcar como favoritas sueltas: viven dentro de su
// módulo. Su `key` sigue existiendo para el resaltado y la analítica.

export const CONTEXT_MENUS = [
  {
    key: 'datacenter',
    label: 'CPD',
    icon: 'datacenter',
    /** Varias rutas de entrada: el módulo agrupa seis vistas que ya existían. */
    pathPrefixes: [
      '/infrastructure/racks',
      '/infrastructure/ups',
      '/infrastructure/pdus',
      '/infrastructure/patch-panels',
      '/infrastructure/power-canvas',
      '/infrastructure/datacenter-diagram',
    ],
    parentSectionKey: 'infrastructure',
    parentSectionLabel: 'Infraestructura',
    items: [
      { key: 'dc-racks', label: 'Racks', path: '/infrastructure/racks' },
      { key: 'dc-pdus', label: 'PDUs', path: '/infrastructure/pdus' },
      { key: 'dc-ups', label: 'SAI (torre y rack)', path: '/infrastructure/ups' },
      { key: 'dc-patch', label: 'Patch Panels', path: '/infrastructure/patch-panels' },
      { key: 'dc-power', label: 'Canvas Alimentación', path: '/infrastructure/power-canvas' },
      { key: 'dc-diagram', label: 'Diagrama Datacenter', path: '/infrastructure/datacenter-diagram' },
    ],
  },
  {
    key: 'workstations',
    label: 'Estaciones de trabajo',
    /** Identificador del icono en el mapa de `Sidebar.jsx`. */
    icon: 'workstations',
    pathPrefix: '/infrastructure/workstations',
    parentSectionKey: 'infrastructure',
    parentSectionLabel: 'Infraestructura',
    items: [
      { key: 'ws-dashboard', label: 'Dashboard', path: '/infrastructure/workstations', end: true },
      { key: 'ws-list', label: 'Equipos', path: '/infrastructure/workstations/list' },
      { key: 'ws-updates', label: 'Actualizaciones', path: '/infrastructure/workstations/updates' },
      { key: 'ws-vulns', label: 'Vulnerabilidades', path: '/infrastructure/workstations/vulnerabilities' },
      { key: 'ws-software', label: 'Software', path: '/infrastructure/workstations/software' },
      { key: 'ws-plans', label: 'Planes de trabajo', path: '/infrastructure/workstations/plans' },
    ],
  },
];

/** Menú de módulo al que pertenece una ruta, o null si no estamos en ninguno. */
export function resolveContextMenuForPath(pathname) {
  if (!pathname || typeof pathname !== 'string') return null;
  const clean = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  const matches = (prefix) => clean === prefix || clean.startsWith(`${prefix}/`);
  return (
    CONTEXT_MENUS.find((menu) => {
      const prefixes = menu.pathPrefixes || (menu.pathPrefix ? [menu.pathPrefix] : []);
      return prefixes.some(matches);
    }) || null
  );
}

